/**
 * RegulatoryNodeDetailPanel — Detail view for a selected regulatory node.
 *
 * Shows jurisdiction, edge type (mandatory vs voluntary), reason, priority,
 * enforcement body, and mandates summary when a regulatory node is clicked
 * in the RegulatoryConstellationGraph.
 *
 * DRR-075: Regulatory constellation visualization.
 */

import React from 'react';
import {
  Badge,
  Group,
  List,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconBuilding,
  IconScale,
  IconAlertTriangle,
} from '@tabler/icons-react';

import type { RegulatoryNode, Jurisdiction } from './hooks/useRegulatoryConstellation';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const JURISDICTION_COLORS: Record<Jurisdiction, string> = {
  federal: 'red',
  state: 'orange',
  payer: 'green',
  standard: 'gray',
};

const JURISDICTION_LABELS: Record<Jurisdiction, string> = {
  federal: 'Federal',
  state: 'State',
  payer: 'Payer',
  standard: 'Standard',
};

const JURISDICTION_ICONS: Record<Jurisdiction, React.ReactNode> = {
  federal: <IconShieldCheck size={16} />,
  state: <IconBuilding size={16} />,
  payer: <IconScale size={16} />,
  standard: <IconAlertTriangle size={16} />,
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RegulatoryNodeDetailPanelProps {
  /** The selected regulatory node to display. */
  node: RegulatoryNode | null;
  /** Callback to close/deselect. */
  onClose?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RegulatoryNodeDetailPanel({
  node,
  onClose,
}: RegulatoryNodeDetailPanelProps) {
  if (!node) return null;

  const jurisdictionColor = JURISDICTION_COLORS[node.jurisdiction];

  return (
    <Paper p="md" withBorder radius="sm">
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <ThemeIcon
              variant="light"
              color={jurisdictionColor}
              size="md"
            >
              {JURISDICTION_ICONS[node.jurisdiction]}
            </ThemeIcon>
            <Title order={5}>{node.name}</Title>
          </Group>
          <Group gap={4}>
            <Badge
              variant="filled"
              color={jurisdictionColor}
              size="sm"
            >
              {JURISDICTION_LABELS[node.jurisdiction]}
            </Badge>
            {node.priority === 'primary' && (
              <Badge variant="filled" color="blue" size="sm">
                Primary
              </Badge>
            )}
            {node.conditional && (
              <Badge variant="outline" color="yellow" size="sm">
                Conditional
              </Badge>
            )}
          </Group>
        </Group>

        {/* Edge type */}
        <Group gap="xs">
          <Text size="sm" fw={600} c="dimmed">
            Compliance:
          </Text>
          <Badge
            variant={
              node.edgeType === 'REGULATED_BY' ? 'filled' : 'outline'
            }
            color={
              node.edgeType === 'REGULATED_BY' ? 'red' : 'gray'
            }
            size="sm"
          >
            {node.edgeType === 'REGULATED_BY'
              ? 'Mandatory'
              : 'Voluntary'}
          </Badge>
        </Group>

        {/* Reason */}
        <Stack gap={2}>
          <Text size="sm" fw={600} c="dimmed">
            Why this applies:
          </Text>
          <Text size="sm">{node.reason}</Text>
        </Stack>

        {/* Notes */}
        {node.notes && (
          <Stack gap={2}>
            <Text size="sm" fw={600} c="dimmed">
              Notes:
            </Text>
            <Text size="xs" c="dimmed" fs="italic">
              {node.notes}
            </Text>
          </Stack>
        )}

        {/* Node ID reference */}
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            Node ID:
          </Text>
          <Badge variant="light" color="gray" size="xs" tt="none">
            {node.nodeId}
          </Badge>
        </Group>
      </Stack>
    </Paper>
  );
}
